import { ArrowRight, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";

import { CodeEntryInput } from "~/components/code-entry-input";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "~/components/ui/card";
import { listSessions, type SessionDetailDto } from "~/lib/api";
import { validateHandoverCode } from "~/lib/codes";
import { formatRemaining, useCountdown } from "~/lib/countdown";

const CODE_LENGTH = 6;

const SHIFT_LABEL: Record<SessionDetailDto["shiftType"], string> = {
  day: "Day shift",
  night: "Night shift",
};

const STATUS_LABEL: Record<SessionDetailDto["status"], string> = {
  draft: "Draft",
  active: "Active",
  // E7.3 — from the doctor's point of view the handover finished
  // successfully; "Locked" is the database state but the wrong vocabulary on
  // a "your recent handovers" list.
  locked: "Complete",
};

function formatStarted(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

/**
 * Discriminated error states surfaced under the code-entry box (HAN-54).
 * `lockedUntil` is computed once when the lockout response lands so the
 * countdown ticks against a fixed wall-clock target across renders.
 */
type CodeError =
  | { kind: "wrong"; attemptsLeft?: number }
  | { kind: "expired" }
  | { kind: "used" }
  | { kind: "locked"; lockedUntil: number }
  | { kind: "self" }
  | { kind: "generic" };

/**
 * The post-login landing (E3.1). The code-entry box is the dominant element so
 * the incoming doctor can't miss it; a quieter link lets the outgoing doctor
 * start a new handover, and any unfinished handovers the user started are listed
 * below so they can resume one. E6.5 wires the box to the validate endpoint and
 * renders an error Alert below the input for every failure mode.
 */
export default function HomeRoute(): JSX.Element {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [recent, setRecent] = useState<SessionDetailDto[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<CodeError | null>(null);

  // The user's own unfinished sessions, for "continue a handover". A failure
  // here leaves the list empty rather than blocking the (primary) code box —
  // resuming a draft is a convenience, not the point of this screen.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // Include `locked` so completed handovers stay visible (read-only) on
      // the recent list — HAN-57 calls for a green "Complete" pill on them.
      const result = await listSessions({
        query: { mine: true, status: "draft,active,locked" },
      });
      if (!cancelled) setRecent(result.data ?? []);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (value: string): void => {
    setCode(value);
    // Per spec: error clears as soon as the doctor types again — they're
    // already correcting the input, so the previous message is just noise.
    if (error) setError(null);
  };

  const handleSubmit = async (value: string): Promise<void> => {
    if (value.length !== CODE_LENGTH || submitting) return;
    setError(null);
    setSubmitting(true);
    const result = await validateHandoverCode(value);
    setSubmitting(false);
    switch (result.status) {
      case "ok":
        navigate(`/session/${result.data.sessionId}`);
        return;
      case "wrong":
        setError({ kind: "wrong", attemptsLeft: result.attemptsLeft });
        return;
      case "expired":
        setError({ kind: "expired" });
        return;
      case "used":
        setError({ kind: "used" });
        return;
      case "locked":
        // Pin the lockout target to a wall-clock instant *now* so the countdown
        // is stable across the renders that follow each tick.
        setError({
          kind: "locked",
          lockedUntil: Date.now() + result.retryAfterSeconds * 1000,
        });
        return;
      case "self":
        setError({ kind: "self" });
        return;
      case "error":
        setError({ kind: "generic" });
        return;
    }
  };

  const complete = code.length === CODE_LENGTH;

  return (
    <section className="mx-auto flex w-full max-w-[600px] flex-col gap-10 py-4 sm:py-8">
      {/* Dominant code-entry box — the first thing the incoming doctor sees. */}
      <Card className="shadow-md">
        <CardHeader className="space-y-1.5 text-center">
          {/* CardTitle is a div; give it heading semantics so it's the page's h1. */}
          <CardTitle role="heading" aria-level={1} className="text-2xl">
            Enter handover code
          </CardTitle>
          <CardDescription>Type the 6-character code your colleague gave you</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 pb-8">
          <CodeEntryInput
            value={code}
            onChange={handleChange}
            onSubmit={(v) => void handleSubmit(v)}
            disabled={submitting}
            autoFocus
            className="[&_input]:h-16 [&_input]:text-3xl"
          />
          {error ? <CodeError error={error} /> : null}
          <Button
            type="button"
            size="lg"
            className="w-full"
            disabled={!complete || submitting}
            onClick={() => void handleSubmit(code)}
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                Checking…
              </>
            ) : (
              "Enter"
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Secondary: the outgoing doctor starts a fresh handover from ward-select. */}
      <div className="text-center">
        <Button asChild variant="link">
          <Link to="/select-ward">
            Or start a new handover
            <ArrowRight />
          </Link>
        </Button>
      </div>

      {/* Only rendered when the user has unfinished handovers — no empty state. */}
      {recent.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-medium text-muted-foreground">Your recent handovers</h2>
          <ul className="space-y-2">
            {recent.map((session) => (
              <li key={session.id}>
                <Link
                  to={`/session/${session.id}`}
                  className="flex items-center justify-between gap-3 rounded-lg border border-border p-4 transition-colors hover:bg-accent"
                >
                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span className="font-medium">{session.wardName}</span>
                    <span className="text-sm text-muted-foreground">
                      {SHIFT_LABEL[session.shiftType]} · Started {formatStarted(session.createdAt)}
                    </span>
                  </span>
                  <span className="flex items-center gap-3">
                    <StatusPill status={session.status} />
                    <ArrowRight className="size-4 shrink-0 text-muted-foreground" />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </section>
  );
}

/**
 * The validation-error Alert. `wrong`/`expired`/`used`/`locked`/`generic` are
 * destructive; `self` is amber (a directional nudge rather than a hard error,
 * since the user's own session is sitting in the list right below the box).
 */
function CodeError({ error }: { error: CodeError }): JSX.Element {
  switch (error.kind) {
    case "wrong":
      return <WrongCodeAlert attemptsLeft={error.attemptsLeft} />;
    case "expired":
      return (
        <Alert variant="destructive">
          <AlertTitle>Code expired</AlertTitle>
          <AlertDescription>
            This code has expired. Ask the outgoing doctor to start a new handover.
          </AlertDescription>
        </Alert>
      );
    case "used":
      return (
        <Alert variant="destructive">
          <AlertTitle>Code already used</AlertTitle>
          <AlertDescription>This code has already been used.</AlertDescription>
        </Alert>
      );
    case "locked":
      return <LockedAlert lockedUntil={error.lockedUntil} />;
    case "self":
      return (
        <Alert
          className="border-amber-300 bg-amber-50 text-amber-900 [&>svg]:text-amber-900 dark:border-amber-300/40 dark:bg-amber-950/40 dark:text-amber-100"
          data-testid="self-cannot-validate-alert"
        >
          <AlertTitle>This is your own session</AlertTitle>
          <AlertDescription>You can find it in the recent-sessions list below.</AlertDescription>
        </Alert>
      );
    case "generic":
      return (
        <Alert variant="destructive">
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>Something went wrong, please try again.</AlertDescription>
        </Alert>
      );
  }
}

function WrongCodeAlert({ attemptsLeft }: { attemptsLeft?: number }): JSX.Element {
  // Special copy when one attempt remains — the next wrong guess will lock the
  // code for 10 minutes, so the warning is louder.
  if (attemptsLeft === 1) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Code is wrong</AlertTitle>
        <AlertDescription>
          <strong>1 attempt left</strong> before lockout.
        </AlertDescription>
      </Alert>
    );
  }
  return (
    <Alert variant="destructive">
      <AlertTitle>Code is wrong</AlertTitle>
      <AlertDescription>
        {typeof attemptsLeft === "number"
          ? `${attemptsLeft} attempts left.`
          : "Please check the code and try again."}
      </AlertDescription>
    </Alert>
  );
}

/**
 * Status chip for one row of the recent-sessions list. Locked = green
 * "Complete" (HAN-57's tell that the handover finished and the screens
 * behind it are read-only); draft/active stay muted so the eye lands on
 * the only one that needs different attention.
 */
function StatusPill({ status }: { status: SessionDetailDto["status"] }): JSX.Element {
  const isLocked = status === "locked";
  return (
    <span
      data-testid={`session-status-pill-${status}`}
      className={
        isLocked
          ? "shrink-0 rounded-full bg-priority-low/15 px-2.5 py-0.5 text-xs font-semibold text-priority-low"
          : "shrink-0 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground"
      }
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

function LockedAlert({ lockedUntil }: { lockedUntil: number }): JSX.Element {
  const remainingMs = useCountdown(lockedUntil);
  // Round UP to the nearest minute so "1 minute" persists for the final 60s of
  // the lockout rather than flicking to "0 minutes" half-way through.
  const minutes = Math.max(1, Math.ceil(remainingMs / 60000));
  const expired = remainingMs <= 0;
  return (
    <Alert variant="destructive">
      <AlertTitle>Too many wrong attempts</AlertTitle>
      <AlertDescription>
        {expired ? (
          "You can try again now."
        ) : (
          <>
            Try again in{" "}
            <span className="font-medium tabular-nums" data-testid="lockout-countdown">
              {minutes === 1 ? "1 minute" : `${minutes} minutes`}
            </span>{" "}
            <span className="text-xs opacity-75 tabular-nums" aria-hidden="true">
              ({formatRemaining(remainingMs)})
            </span>
          </>
        )}
      </AlertDescription>
    </Alert>
  );
}
